import { useCallback, useMemo, useRef, useState } from 'react'
import { type FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangleIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  FileIcon,
  FileUpIcon,
  ImageIcon,
  ImageUpIcon,
  InfoIcon,
  Loader2,
  XIcon,
} from 'lucide-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { discardOnUnload } from '@/api/discard-on-unload'
import { useAddFilesToDocument } from '@/api/hooks/useAddFilesToDocument'
import { useDraftDocumentsLiveSync } from '@/api/hooks/useDraftDocumentsLiveSync'
import { useCommitDocument } from '@/api/hooks/useCommitDocument'
import { useCreateDocuments } from '@/api/hooks/useCreateDocuments'
import { useDeleteDocument } from '@/api/hooks/useDeleteDocument'
import { useDeleteFile } from '@/api/hooks/useDeleteFile'
import { useIndividualsServiceGetV1IndividualsByIndividualIdKey } from '@/api/client/queries/common'
import { useRenameDocument } from '@/api/hooks/useRenameDocument'
import { useReorderFiles } from '@/api/hooks/useReorderFiles'
import { useRunPreChecks } from '@/api/hooks/useRunPreChecks'
import { DropzoneOverlay } from '@/components/molecules/dropzone-overlay'
import { FileTypeIcon } from '@/components/molecules/file-type-icon'
import { LeaveDraftsGuard } from '@/components/molecules/leave-drafts-guard'
import PageHeader from '@/components/molecules/page-header'
import { PreCheckConfirmDialog } from '@/components/organisms/pre-check-confirm-dialog'
import { PreCheckWarningList } from '@/components/organisms/pre-check-warnings'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { appConfig } from '@/config/app.config'
import { useIndividuals } from '@/hooks/useIndividuals'
import { usePreviewDocument } from '@/hooks/useDocumentPreview'
import { useStringSet } from '@/hooks/useStringSet'
import { uploadToStoragePath } from '@/integrations/firebase'

export const Route = createFileRoute('/_authenticated/documents/combine')({
  component: DocumentsCombinePage,
})

type DocFile = {
  id?: string
  name: string
  originalName: string
  filePath: string
  mimeType: string
  bytes: number
  order: number
  uploaded: boolean
}

function DocumentsCombinePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedIndividualId, selectedIndividual } = useIndividuals()
  const previewDocument = usePreviewDocument()

  const createDocuments = useCreateDocuments()
  const addFilesToDocument = useAddFilesToDocument()
  const deleteDocument = useDeleteDocument()
  const deleteFile = useDeleteFile()
  const reorderFiles = useReorderFiles()
  const renameDocument = useRenameDocument()
  const commitDocument = useCommitDocument()
  const runPreChecks = useRunPreChecks()

  // The single combined Document being assembled. Created lazily on first drop.
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [name, setName] = useState('')

  useDraftDocumentsLiveSync(selectedIndividualId ?? undefined, documentId ? [documentId] : [])

  // Per-file abort controllers for in-flight cancel.
  const abortControllers = useRef(new Map<string, AbortController>())
  const [uploadingIds, markUploading, markFinished] = useStringSet()
  // Optimistic placeholders for files just dropped — rendered immediately so
  // the rows don't wait for the createDocuments / addFiles round-trip. Each
  // entry is filtered out of the merged file list as soon as the real cached
  // file (matched by originalName) appears. Dedupe by originalName alone (not
  // bytes) so HEIC→JPEG trigger conversion (which changes bytes) doesn't leave
  // a phantom placeholder row.
  const [pendingDrops, setPendingDrops] = useState<
    Array<{ tempId: string; name: string; mimeType: string; bytes: number }>
  >([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const document = useMemo(
    () =>
      selectedIndividual?.documents?.find(d => d.id === documentId && d.status === 'draft') ?? null,
    [selectedIndividual?.documents, documentId],
  )

  const files = useMemo<DocFile[]>(() => {
    const real = (document?.files ?? []).slice().sort((a, b) => a.order - b.order) as DocFile[]
    const realKeys = new Set(real.map(f => f.originalName))
    const pending: DocFile[] = pendingDrops
      .filter(p => !realKeys.has(p.name))
      .map((p, i) => ({
        name: p.name,
        originalName: p.name,
        filePath: '',
        mimeType: p.mimeType,
        bytes: p.bytes,
        order: real.length + i,
        uploaded: false,
      }))
    return [...real, ...pending]
  }, [document?.files, pendingDrops])

  const hasFiles = files.length > 0
  const preChecks = document?.preChecks
  const preChecksStatus = preChecks?.status

  // Readiness keys off the backend truth (f.uploaded === uploadStatus UPLOADED),
  // which for HEIC only flips AFTER conversion. We deliberately don't use an
  // optimistic client flag here — that produced a window where a still-converting
  // HEIC looked ready and previewed the unrenderable .heic blob.
  const allUploaded = files.length > 0 && files.every(f => f.uploaded)

  const startFileUpload = useCallback(
    async (fileId: string, filePath: string, file: File) => {
      const controller = new AbortController()
      abortControllers.current.set(fileId, controller)
      markUploading(fileId)
      try {
        await uploadToStoragePath(filePath, file, controller.signal)
        // Don't optimistically mark ready — readiness is f.uploaded, which the
        // backend only sets after the storage trigger lands (and converts HEIC).
        // The refetch below + the backend's filesUpdatedAt RTDB push surface it.
        queryClient.invalidateQueries({
          queryKey: [useIndividualsServiceGetV1IndividualsByIndividualIdKey],
        })
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        console.error('Upload failed:', err)
        toast.error(t('combinePage.uploadFailed', { name: file.name }))
      } finally {
        abortControllers.current.delete(fileId)
        markFinished(fileId)
      }
    },
    [t, queryClient, markUploading, markFinished],
  )

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!selectedIndividualId || acceptedFiles.length === 0) return
      const tempDrops = acceptedFiles.map(f => ({
        tempId: `pending-${Date.now()}-${Math.random()}`,
        name: f.name,
        mimeType: f.type,
        bytes: f.size,
      }))
      const tempIds = new Set(tempDrops.map(t => t.tempId))
      setPendingDrops(prev => [...prev, ...tempDrops])
      try {
        if (documentId == null) {
          // First drop: create the Document with the first file. Subsequent
          // files (if multiple were dropped at once) go through addFiles.
          const first = acceptedFiles[0]!
          const rest = acceptedFiles.slice(1)
          const [created] = await createDocuments.mutateAsync({
            individualId: selectedIndividualId,
            requestBody: [
              {
                name: name.trim() || first.name,
                files: [{ originalName: first.name, mimeType: first.type, bytes: first.size }],
              },
            ],
          })
          if (!created) return
          setDocumentId(created.id)
          const firstFileInfo = created.files[0]
          if (firstFileInfo) {
            await startFileUpload(firstFileInfo.fileId, firstFileInfo.filePath, first)
          }
          if (rest.length > 0) {
            const added = await addFilesToDocument.mutateAsync({
              individualId: selectedIndividualId,
              documentId: created.id,
              requestBody: {
                files: rest.map(f => ({
                  originalName: f.name,
                  mimeType: f.type,
                  bytes: f.size,
                })),
              },
            })
            await Promise.all(
              added.files.map((info, i) => startFileUpload(info.fileId, info.filePath, rest[i]!)),
            )
          }
        } else {
          // Subsequent drops: append to the existing Document.
          const added = await addFilesToDocument.mutateAsync({
            individualId: selectedIndividualId,
            documentId,
            requestBody: {
              files: acceptedFiles.map(f => ({
                originalName: f.name,
                mimeType: f.type,
                bytes: f.size,
              })),
            },
          })
          await Promise.all(
            added.files.map((info, i) =>
              startFileUpload(info.fileId, info.filePath, acceptedFiles[i]!),
            ),
          )
        }
      } catch (err) {
        console.error('Combine drop failed:', err)
        toast.error(t('combinePage.dropFailed'))
      } finally {
        setPendingDrops(prev => prev.filter(p => !tempIds.has(p.tempId)))
      }
    },
    [
      selectedIndividualId,
      documentId,
      name,
      createDocuments,
      addFilesToDocument,
      startFileUpload,
      t,
    ],
  )

  const dropzone = useDropzone({
    accept: appConfig.documentUpload.allowedFileTypes,
    maxSize: appConfig.documentUpload.maxUploadSize,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDropRejected: (rejections: FileRejection[]) => {
      toast.error(
        t('combinePage.fileRejected', {
          mb: appConfig.documentUpload.maxUploadSize / (1024 * 1024),
        }),
      )
      console.log('fileRejections', rejections)
    },
  })

  const moveFile = useCallback(
    async (fileId: string, direction: -1 | 1) => {
      if (!selectedIndividualId || !documentId) return
      const idx = files.findIndex(f => f.id === fileId)
      if (idx < 0) return
      const targetIdx = idx + direction
      if (targetIdx < 0 || targetIdx >= files.length) return
      const next = files.slice()
      ;[next[idx], next[targetIdx]] = [next[targetIdx]!, next[idx]!]
      await reorderFiles.mutateAsync({
        individualId: selectedIndividualId,
        documentId,
        requestBody: { files: next.map((f, i) => ({ fileId: f.id!, order: i })) },
      })
    },
    [files, selectedIndividualId, documentId, reorderFiles],
  )

  const handleRemoveFile = useCallback(
    async (fileId: string) => {
      if (!selectedIndividualId || !documentId) return
      // Capture documentId — if this delete removes the last file we reset
      // documentId to null mid-flight and still need the original id.
      const docIdAtStart = documentId
      abortControllers.current.get(fileId)?.abort()

      // The row is dropped from the cache before this resolves (see
      // `useDeleteFile`), so spam-deleting doesn't queue each click behind the
      // previous one's refetch. The endpoint is idempotent, so a repeat delete of
      // an already-removed file succeeds rather than 404ing; a genuine failure
      // rolls the row back and toasts.
      try {
        const res = await deleteFile.mutateAsync({
          individualId: selectedIndividualId,
          documentId: docIdAtStart,
          fileId,
        })
        if (res.deleted === 'document') {
          setDocumentId(null)
          setName('')
        }
      } catch (err) {
        console.error('Delete failed:', err)
        toast.error(t('uploadPage.deleteFailed'))
      }
    },
    [selectedIndividualId, documentId, deleteFile, t],
  )

  const handlePreview = useCallback(
    (file: DocFile) => {
      previewDocument(file.filePath, file.mimeType)
    },
    [previewDocument],
  )

  const handleCancel = useCallback(async () => {
    if (!selectedIndividualId) {
      navigate({ to: '/documents/upload' })
      return
    }
    // Abort all in-flight, delete the document if one exists, navigate away.
    for (const controller of abortControllers.current.values()) controller.abort()
    abortControllers.current.clear()
    if (documentId) {
      await deleteDocument.mutateAsync({ individualId: selectedIndividualId, documentId })
    }
    navigate({ to: '/documents/upload', ignoreBlocker: true })
  }, [selectedIndividualId, documentId, deleteDocument, navigate])

  const performAnalyze = useCallback(async () => {
    if (!selectedIndividualId || !documentId) return
    setIsAnalyzing(true)
    try {
      if (document && document.name !== name.trim()) {
        await renameDocument.mutateAsync({
          individualId: selectedIndividualId,
          documentId,
          requestBody: { name: name.trim() },
        })
      }
      await commitDocument.mutateAsync({ individualId: selectedIndividualId, documentId })
      navigate({ to: '/documents', ignoreBlocker: true })
    } catch (err) {
      console.error('Analyze failed:', err)
      toast.error(t('combinePage.analyzeFailed'))
    } finally {
      setIsAnalyzing(false)
    }
  }, [
    selectedIndividualId,
    documentId,
    name,
    document,
    renameDocument,
    commitDocument,
    navigate,
    t,
  ])

  const handleAnalyze = useCallback(async () => {
    if (!selectedIndividualId || !documentId) return
    if (!name.trim()) {
      toast.error(t('combinePage.nameRequired'))
      return
    }
    if (!allUploaded) {
      toast.error(t('combinePage.stillUploading'))
      return
    }
    setIsAnalyzing(true)
    try {
      const result = await runPreChecks.mutateAsync({
        individualId: selectedIndividualId,
        documentId,
      })
      if (result.status === 'warning' && (result.results?.warnings?.length ?? 0) > 0) {
        setConfirmOpen(true)
        return
      }
      await performAnalyze()
    } catch (err) {
      console.error('Pre-checks failed:', err)
      toast.error(t('combinePage.analyzeFailed'))
    } finally {
      setIsAnalyzing(false)
    }
  }, [selectedIndividualId, documentId, name, allUploaded, runPreChecks, performAnalyze, t])

  return (
    <>
      <div {...dropzone.getRootProps()} className="space-y-6 z-10">
        <input className="hidden pointer-events-none" {...dropzone.getInputProps()} />
        <PageHeader
          body={t('uploadPage.header.description')}
          heading={t('uploadPage.header.title')}
        />
        <div className="space-y-2">
          <h4 className="text-base font-bold">{t('uploadPage.tab.heading')}</h4>
          <Tabs value="combine">
            <TabsList>
              <TabsTrigger value="upload" onClick={() => navigate({ to: '/documents/upload' })}>
                {t('uploadPage.tab.upload.label')}
              </TabsTrigger>
              <TabsTrigger value="combine">{t('uploadPage.tab.combine.label')}</TabsTrigger>{' '}
            </TabsList>{' '}
          </Tabs>{' '}
        </div>{' '}
        {!hasFiles ? (
          <>
            {' '}
            <div className="space-y-2">
              {' '}
              <div
                onClick={dropzone.open}
                className="flex flex-col items-center justify-center gap-4 p-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent transition-colors"
              >
                {' '}
                <ImageUpIcon className="size-12 text-text-secondary" />{' '}
                <p className="text-lg font-medium">{t('combinePage.dropzone.title')}</p>
                <p className="text-base text-text-secondary max-w-sm text-center">
                  {t('combinePage.dropzone.subtitle')}
                </p>
              </div>
              <p className="text-xs text-text-secondary">{t('uploadPage.dropzone.formats')}</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-sm font-medium">
                {t('combinePage.nameLabel')}
                <span className="text-destructive ml-0.5">*</span>
              </label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('combinePage.namePlaceholder')}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('combinePage.filesLabel')}</label>
              <div className="space-y-3">
                {files.map((f, idx) => (
                  <div
                    key={f.id ?? idx}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-card transition-colors hover:bg-layer-2"
                  >
                    <span className="text-xs text-text-secondary tabular-nums shrink-0 w-5 text-right">
                      {idx + 1}.
                    </span>
                    <FileTypeIcon
                      mimeType={f.mimeType}
                      className="size-5 text-text-secondary shrink-0"
                    />
                    <span className="text-sm flex-1 truncate">{f.originalName}</span>
                    <div className="w-5 flex items-center justify-center shrink-0">
                      {!f.id || uploadingIds.has(f.id) || !f.uploaded ? (
                        <Loader2 className="size-4 animate-spin text-text-secondary" />
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-layer-1"
                            onClick={() => moveFile(f.id!, -1)}
                            disabled={!f.id || idx === 0}
                            aria-label={t('combinePage.moveUp')}
                          >
                            <ArrowUpIcon />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('combinePage.moveUp')}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-layer-1"
                            onClick={() => moveFile(f.id!, 1)}
                            disabled={!f.id || idx === files.length - 1}
                            aria-label={t('combinePage.moveDown')}
                          >
                            <ArrowDownIcon />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('combinePage.moveDown')}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-layer-1"
                            onClick={() => handlePreview(f)}
                            disabled={!f.uploaded}
                            aria-label={t('documentsTable.actions.openDocument')}
                          >
                            {f.mimeType.startsWith('image/') ? <ImageIcon /> : <FileIcon />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('documentsTable.actions.openDocument')}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="hover:bg-layer-1"
                            onClick={() => f.id && handleRemoveFile(f.id)}
                            aria-label={t('combinePage.remove')}
                          >
                            <XIcon />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('uploadPage.delete')}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={dropzone.open}
                  className="w-full p-6 border-2 border-dashed rounded-lg text-text-secondary text-sm hover:bg-layer-1 transition-colors"
                >
                  {t('combinePage.addMore')}
                </button>
              </div>
            </div>
          </>
        )}
        {hasFiles && (
          <Alert variant="info">
            <InfoIcon />
            <AlertDescription>{t('uploadPage.processingInfo')}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sticky bottom-0 bg-background py-4">
          <Button
            onClick={handleAnalyze}
            disabled={
              !hasFiles || !name.trim() || !allUploaded || isAnalyzing || deleteFile.isPending
            }
            className="w-full sm:w-auto"
          >
            {isAnalyzing ? <Loader2 className="animate-spin" /> : <FileUpIcon />}
            {isAnalyzing ? t('preCheckRunning') : t('combinePage.analyze')}
          </Button>
        </div>
      </div>

      <DropzoneOverlay
        isDragActive={dropzone.isDragActive}
        message={t('documentsPage.dropzoneMessage')}
      />
      <PreCheckConfirmDialog
        open={confirmOpen}
        codes={preChecks?.results?.warnings ?? []}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          performAnalyze()
        }}
      />
      <LeaveDraftsGuard
        hasPendingDrafts={documentId !== null}
        onConfirmDiscard={async () => {
          if (!selectedIndividualId) return
          for (const controller of abortControllers.current.values()) controller.abort()
          abortControllers.current.clear()
          if (documentId) {
            await deleteDocument.mutateAsync({
              individualId: selectedIndividualId,
              documentId,
            })
          }
        }}
        onUnloadDiscard={() =>
          selectedIndividualId &&
          documentId &&
          discardOnUnload(`/v1/individuals/${selectedIndividualId}/documents/${documentId}`)
        }
      />
    </>
  )
}
